import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { useToast } from '@/src/components/Toast';

import {
  APP_VERSION_CHECK_INTERVAL_MS,
  appVersionsDiffer,
  currentLoadedAppVersion,
  fetchLatestAppVersion,
} from './app-version-service';

function isDevelopmentBuild() {
  return typeof __DEV__ !== 'undefined' && __DEV__;
}

function refreshToLatestApp() {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('__frienzzi_update', String(Date.now()));
  window.location.replace(nextUrl.toString());
}

export function AppVersionUpdatePrompt() {
  const { showToast } = useToast();
  const loadedVersionRef = useRef(currentLoadedAppVersion()?.version ?? null);
  const promptShownRef = useRef(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      isDevelopmentBuild()
    ) {
      return undefined;
    }

    let disposed = false;

    async function checkForUpdate() {
      try {
        const latestVersion = await fetchLatestAppVersion();

        if (disposed || !latestVersion) {
          return;
        }

        if (!loadedVersionRef.current) {
          loadedVersionRef.current = latestVersion.version;
          return;
        }

        if (
          appVersionsDiffer(loadedVersionRef.current, latestVersion.version)
        ) {
          setUpdateAvailable(true);
        }
      } catch {
        // Version checks are best-effort. A failed check should not interrupt app use.
      }
    }

    function checkWhenVisible() {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    }

    const interval = window.setInterval(
      checkForUpdate,
      APP_VERSION_CHECK_INTERVAL_MS,
    );

    void checkForUpdate();
    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkWhenVisible);
    window.addEventListener('online', checkWhenVisible);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.removeEventListener('focus', checkWhenVisible);
      window.removeEventListener('online', checkWhenVisible);
    };
  }, []);

  useEffect(() => {
    if (!updateAvailable || promptShownRef.current) {
      return;
    }

    promptShownRef.current = true;
    showToast({
      actionLabel: 'Refresh',
      durationMs: null,
      message: 'Tap to refresh Frienzzi and load the newest version.',
      onPress: refreshToLatestApp,
      title: 'Update available',
      variant: 'info',
    });
  }, [showToast, updateAvailable]);

  return null;
}
